import React, { useEffect, useState, useRef } from 'react';
import { getAIExplanation, getFrequentlyAsked } from '../api';
import { useLocation } from 'react-router-dom';
import generateAIAnim from '../animations/generate_ai_answer.json';
import Lottie from 'lottie-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { getQuestionsV2 } from '../api';
import { trackEvent } from '../utils/analytics';
import AIExplanationRenderer from './AIExplanationRenderer';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

function QuestionList() {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [aiExplanation, setAiExplanation] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;
  const [loadingPage, setLoadingPage] = useState(false);
  const [rateLimitReached, setRateLimitReached] = useState(false);
  const [remainingFreeUses, setRemainingFreeUses] = useState(null);
  const [frequentGroups, setFrequentGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const completionFiredRef = useRef(false);
  const query = useQuery();
  const selectedBoard = query.get("board");
  const selectedGrade = query.get("grade");
  const selectedSubject = query.get("subject");
  const category = query.get("category");
  const selectedProgram = query.get("program");
  const selectedMonth = query.get("month");
  const selectedMode = query.get("mode");
  const selectedStream = query.get("stream");
  const qSearch = query.get("q") || "";

  const isFrequentMode = selectedMode === 'frequent';

  const toggleGroupExpand = (groupIndex) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupIndex]: !prev[groupIndex]
    }));
  };

  const handleGroupQuestionClick = (groupIndex, question) => {
    const flatQuestions = frequentGroups[groupIndex].questions.map((q, idx) => ({
      ...q,
      is_frequent: true,
      frequency: frequentGroups[groupIndex].frequency,
      unique_years: frequentGroups[groupIndex].unique_years,
      display_title: frequentGroups[groupIndex].display_title,
      canonical_text: frequentGroups[groupIndex].canonical_text,
      question_type: 'Most Repeated',
      answer: ''
    }));
    setQuestions(flatQuestions);
    const qIndex = flatQuestions.findIndex(q => q.id === question.id);
    setCurrentIndex(qIndex >= 0 ? qIndex : 0);
    setTotalPages(1);
  };

  useEffect(() => {
    const fetchPage = async () => {
      setLoadingPage(true);

    try {
      if (isFrequentMode) {
        const res = await getFrequentlyAsked({
          category,
          program: selectedProgram,
          board: selectedBoard,
          stream: selectedStream,
          grade: selectedGrade,
          subject: selectedSubject,
          limit: perPage,
          q: qSearch
        });

        setFrequentGroups(res);
        setExpandedGroups({});
        trackEvent("frequent_questions_view", {
          board: selectedBoard,
          grade: selectedGrade,
          subject: selectedSubject,
          groups: res.length
        });
        setQuestions([]);
        setTotalPages(1);
        setCurrentIndex(0);
      } else {
        const res = await getQuestionsV2({
          category,
          program: selectedProgram,
          board: selectedBoard,
          stream: selectedStream,
          grade: selectedGrade,
          subject: selectedSubject,
          year: query.get("year"),
          term: query.get("term"),
          month: selectedMonth,
          page,
          per_page: perPage
        });

        setQuestions(res.questions);
        setTotalPages(res.total_pages);
        setCurrentIndex(0);
        trackEvent("question_list_view", {
          board: selectedBoard,
          grade: selectedGrade,
          subject: selectedSubject,
          page: page
        });
      }

    } catch (err) {
      if (err?.response?.status === 429) {
        setRateLimitReached(true);
        return;
      }
      console.error(err);
    } finally {
      setLoadingPage(false);
    }

    };

    fetchPage();
  }, [selectedBoard, selectedGrade, selectedSubject, selectedStream, selectedProgram, selectedMode, page]);

  const currentQuestion = questions[currentIndex];

  const globalIndex = isFrequentMode 
    ? currentIndex + 1 
    : (page - 1) * perPage + currentIndex + 1;

  useEffect(() => {
    if (currentQuestion) {
      trackEvent("question_viewed", {
        question_id: currentQuestion.id,
        question_no: currentQuestion.question_no,
        board: currentQuestion.board,
        grade: currentQuestion.grade,
        subject: currentQuestion.subject,
        marks: currentQuestion.marks,
        global_index: globalIndex
      });
    }
  }, [currentIndex, page]);

  const handlePrevious = () => {
    trackEvent("previous_question", {
      question_id: currentQuestion?.id,
      question_no: currentQuestion?.question_no,
      from_index: globalIndex,
      subject: selectedSubject,
      grade: selectedGrade
    });

    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    } else if (page > 1) {
      setPage(prev => prev - 1);
    }
    setShowAnswer(false);
    setShowExplanation(false);
    setAiExplanation("");
    setAiLoading(false);
    setRemainingFreeUses(null);
  };

  const handleNext = () => {
    trackEvent("next_question", {
      question_id: currentQuestion?.id,
      question_no: currentQuestion?.question_no,
      from_index: globalIndex,
      subject: selectedSubject,
      grade: selectedGrade
    });
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    else if (page < totalPages && !loadingPage) {
      setPage(prev => prev + 1);
    }
    setShowAnswer(false);
    setShowExplanation(false);
    setAiExplanation("");
    setAiLoading(false);
    setRemainingFreeUses(null);
  };

  const handleAIExplain = async () => {
    trackEvent("ai_explanation_requested", {
      question_id: currentQuestion.id,
      question_no: currentQuestion.question_no,
      subject: currentQuestion.subject,
      grade: currentQuestion.grade,
      board: currentQuestion.board,
      marks: currentQuestion.marks
    });
    setAiLoading(true);

    try {
      const result = await getAIExplanation({
        text: currentQuestion.text,
        marks: currentQuestion.marks,
        board: currentQuestion.board,
        grade: currentQuestion.grade,
        subject: currentQuestion.subject,
        year: currentQuestion.year
      });
      setAiExplanation(result.ai_explanation);
      setRemainingFreeUses(
        result.remaining_free_uses != null
          ? Number(result.remaining_free_uses)
          : null
      );
      trackEvent("ai_explanation_completed", {
        question_id: currentQuestion.id,
        question_no: currentQuestion.question_no,
        board: currentQuestion.board,
        grade: currentQuestion.grade,
        subject: currentQuestion.subject
      });
    } catch (err) {
      if (err?.response?.status === 429) {
        setRateLimitReached(true);
        trackEvent("ai_limit_reached", {
          feature: "question_explanation",
          board: currentQuestion.board,
          grade: currentQuestion.grade,
          subject: currentQuestion.subject
        });
        trackEvent("login_prompt_shown", {
          reason: "ai_limit",
          feature: "question_explanation"
        });
        return;
      }
      trackEvent("ai_explanation_failed", {
        question_id: currentQuestion.id,
        board: currentQuestion.board,
        grade: currentQuestion.grade,
        subject: currentQuestion.subject
      });
      console.error(err);
    } finally {
      setAiLoading(false);
    }

  };

  if (rateLimitReached) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ background: '#f3f4f6', borderRadius: '24px', padding: '40px 30px', maxWidth: '400px', textAlign: 'center', border: '1px solid #e5e7eb', boxShadow: '0 12px 40px rgba(0,0,0,0.06)' }}>
          <h2 style={{ color: '#111827', marginBottom: '12px' }}>Free Limit Reached</h2>
          <p style={{ color: '#6b7280', marginBottom: '20px' }}>You have reached your free question limit.</p>
          <motion.button onClick={() => window.location.href = "/signup"} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: "14px 28px", borderRadius: '12px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: "#fff", border: "none", cursor: "pointer", fontSize: '15px', fontWeight: 600, boxShadow: '0 4px 15px rgba(16,185,129,0.3)' }}>
            Create Free Account
          </motion.button>
          <div style={{ marginTop: '20px' }}><a href="/login" style={{ color: '#6366f1', fontWeight: 500 }}>Already have an account? Login</a></div>
        </div>
      </div>
    );
  }

  if (loadingPage) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}><Lottie animationData={generateAIAnim} style={{ width: 150, height: 150 }} loop /><p style={{ color: '#6b7280', fontSize: '15px', marginTop: '-10px' }}>Loading questions...</p></div>
      </div>
    );
  }

  if (isFrequentMode && frequentGroups.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
        <div style={{ background: '#f3f4f6', borderRadius: '24px', padding: '40px 30px', maxWidth: '400px', textAlign: 'center', border: '1px solid #e5e7eb', boxShadow: '0 12px 40px rgba(0,0,0,0.06)' }}>
          <h2 style={{ color: '#111827', marginBottom: '12px' }}>No Questions Found</h2>
          <p style={{ color: '#6b7280' }}>Try selecting a different subject or board.</p>
        </div>
      </div>
    );
  }

  if (isFrequentMode && frequentGroups.length > 0 && questions.length === 0) {
    return (
      <MathJaxContext version={3} config={{ tex: { inlineMath: [['$', '$'], ['\\(', '\\)']], displayMath: [['$$', '$$'], ['\\[', '\\]']] }, }}>
        <div style={{ minHeight: '100vh', background: '#f8f9fc', padding: '40px 20px' }}>
          <div style={{ maxWidth: '800px', margin: 'auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h2 style={{ fontSize: '26px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>Frequently Asked Questions</h2>
              <p style={{ color: '#6b7280', fontSize: '15px' }}>{selectedSubject} - {selectedBoard} Grade {selectedGrade} - {frequentGroups.length} Topics</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {frequentGroups.map((group, groupIndex) => {
                const isExpanded = expandedGroups[groupIndex];
                return (
                  <motion.div key={groupIndex} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: groupIndex * 0.05 }} style={{ background: '#f3f4f6', backdropFilter: 'blur(20px)', borderRadius: '16px', boxShadow: '0 8px 30px rgba(0,0,0,0.06)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                    <motion.div onClick={() => toggleGroupExpand(groupIndex)} style={{ padding: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: isExpanded ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white' }} whileHover={{ opacity: 0.95 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}><span style={{ fontSize: '17px', fontWeight: 700 }}>{group.display_title || group.canonical_text}</span></div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '13px', opacity: 0.9 }}>
                          <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 10px', borderRadius: '20px', fontWeight: 500 }}>{group.frequency}x Repeated</span>
                          <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 10px', borderRadius: '20px', fontWeight: 500 }}>{group.years?.slice(0, 3).join(', ')}</span>
                          <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 10px', borderRadius: '20px', fontWeight: 500 }}>{group.marks_range}</span>
                        </div>
                      </div>
                      <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.3 }} style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '14px', color: 'white' }}>v</span></motion.div>
                    </motion.div>
                    <AnimatePresence>{isExpanded && (<motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} style={{ overflow: 'hidden' }}><div style={{ padding: '20px', background: '#f3f4f6' }}><p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px', fontWeight: 500 }}>{group.questions.length} similar questions found:</p><div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>{group.questions.slice(0, 5).map((q, qIdx) => (<motion.div key={q.id} onClick={() => handleGroupQuestionClick(groupIndex, q)} style={{ background: '#f3f4f6', borderRadius: '12px', padding: '15px', cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb', transition: 'all 0.2s ease' }} whileHover={{ scale: 1.01, boxShadow: '0 4px 16px rgba(99,102,241,0.15)' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}><span style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', color: '#1d4ed8', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>Year {q.year}</span>{q.marks && (<span style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', color: '#b45309', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>{q.marks} marks</span>)}</div><MathJax dynamic><div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6' }}><span dangerouslySetInnerHTML={{ __html: q.text }} /></div></MathJax>{q.term && (<div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Term: {q.term}</div>)}</motion.div>))}</div>{group.questions.length > 5 && (<div style={{ textAlign: 'center', marginTop: '16px' }}><span style={{ color: '#6366f1', fontSize: '14px', fontWeight: 600 }}>+ {group.questions.length - 5} more questions</span></div>)}</div></motion.div>)}</AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </MathJaxContext>
    );
  }

  if (!currentQuestion) {
    if (!completionFiredRef.current) {
      completionFiredRef.current = true;
      trackEvent("question_set_completed", { board: selectedBoard, grade: selectedGrade, subject: selectedSubject });
    }
    return (
      <div style={{ minHeight: '100vh', background: '#f8f9fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#f3f4f6', borderRadius: '24px', padding: '40px 30px', maxWidth: '400px', textAlign: 'center', border: '1px solid #e5e7eb', boxShadow: '0 12px 40px rgba(0,0,0,0.06)' }}><h2 style={{ color: '#111827', marginBottom: '12px' }}>All Questions Completed!</h2><p style={{ color: '#6b7280' }}>Great job!</p></div>
      </div>
    );
  }

  return (
    <MathJaxContext version={3} config={{ tex: { inlineMath: [['$', '$'], ['\\(', '\\)']], displayMath: [['$$', '$$'], ['\\[', '\\]']] }, }}>
    <div style={{ minHeight: '100vh', background: '#f8f9fc', padding: '30px 20px' }}>
      <div style={{ maxWidth: '700px', margin: 'auto' }}>
        {isFrequentMode && currentQuestion && (<motion.button onClick={() => { setQuestions([]); setExpandedGroups({}); }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#6b7280', cursor: 'pointer', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '10px', marginBottom: '16px' }}>Back to Topics</motion.button>)}
        
        <div style={{ background: '#f3f4f6', backdropFilter: 'blur(20px)', borderRadius: '20px', padding: '24px', border: '1px solid #e5e7eb', boxShadow: '0 12px 40px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <div><h2 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: 0 }}>{selectedSubject}</h2><p style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{category === "school" ? `Grade ${selectedGrade}` : `Semester ${selectedGrade}`} - {selectedBoard}</p></div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {currentQuestion.marks && (<div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', color: '#b45309', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}>{currentQuestion.marks} Marks</div>)}
              <div style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', padding: '8px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: 600 }}>Q{globalIndex}</div>
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ height: '6px', background: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}><motion.div style={{ height: '100%', width: `${((currentIndex + 1) / questions.length) * 100}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width 0.4s ease' }} /></div>
            <p style={{ textAlign: 'right', margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>{currentIndex + 1} of {questions.length}</p>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={currentIndex} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} style={{ background: '#f3f4f6', borderRadius: '16px', padding: '24px', border: '1px solid #e5e7eb' }}>
              {currentQuestion.instructions && (<div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', border: '1px solid rgba(245,158,11,0.2)', color: '#92400e', fontSize: '14px' }}>Instructions: {currentQuestion.instructions}</div>)}
              <MathJax dynamic><div style={{ whiteSpace: 'pre-line', fontSize: '16px', lineHeight: '1.7', color: '#111827' }}><strong>{currentQuestion.question_no || `Q${currentIndex + 1}`}.</strong> <span dangerouslySetInnerHTML={{ __html: currentQuestion.text }} /></div></MathJax>
              {isFrequentMode && currentQuestion.year && (<div style={{ marginTop: '12px', fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', color: '#1d4ed8', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '12px' }}>Year {currentQuestion.year}</span></div>)}
              {currentQuestion.media?.length > 0 && (<div style={{ margin: '20px 0' }}>{currentQuestion.media.filter(m => m.media_type === 'image').map((m, i) => (<motion.img key={i} src={m.url} alt="Question visual" whileHover={{ scale: 1.01 }} style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', marginBottom: '10px' }} />))}</div>)}
              
              {currentQuestion.options?.length > 0 && (<MathJax dynamic><ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '16px' }}>{currentQuestion.options.map(opt => (<li key={opt.label} style={{ padding: '12px', marginBottom: '8px', background: '#f3f4f6', borderRadius: '10px', border: '1px solid #e5e7eb' }}><strong style={{ color: '#6366f1' }}>{opt.label}.</strong> <span dangerouslySetInnerHTML={{ __html: opt.text }} /></li>))}</ul></MathJax>)}
              {remainingFreeUses != null && (<div style={{ marginTop: '16px', padding: "12px 16px", background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)", borderRadius: "10px", fontSize: "13px", color: "#92400e", fontWeight: 500, border: '1px solid rgba(245,158,11,0.2)' }}>You have {remainingFreeUses} free AI explanations left.</div>)}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px' }}>
                {!showAnswer && (<motion.button onClick={() => { trackEvent("show_answer_clicked", { question_id: currentQuestion.id, question_no: currentQuestion.question_no, subject: selectedSubject, grade: selectedGrade }); setShowAnswer(true); }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', boxShadow: '0 4px 15px rgba(99,102,241,0.3)' }}>Show Answer</motion.button>)}
                {showAnswer && currentQuestion.answers?.length > 0 && (<><MathJax><div style={{ marginTop: '16px', background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid rgba(34,197,94,0.2)', width: '100%' }}><strong style={{ color: '#166534', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>Answer</strong><span style={{ color: '#166534', lineHeight: '1.6' }}>{currentQuestion.answers[0].answer_text}</span></div></MathJax>{!showExplanation && (<motion.button onClick={() => { trackEvent("show_explanation_clicked", { question_id: currentQuestion.id, question_no: currentQuestion.question_no, subject: selectedSubject, grade: selectedGrade }); setShowExplanation(true); }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', boxShadow: '0 4px 15px rgba(139,92,246,0.3)', marginTop: '12px' }}>Show Explanation</motion.button>)}</>)}
                {showExplanation && (<MathJax><div style={{ marginTop: '16px', background: '#f0f9ff', padding: '16px', borderRadius: '12px', border: '1px solid rgba(14,165,233,0.2)', width: '100%' }}><strong style={{ color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>Explanation</strong><span style={{ color: '#075985', lineHeight: '1.6' }}>{(currentQuestion.answers?.[0]?.explanation || "No explanation available.").replace(/\\\\/g, "\\")}</span></div></MathJax>)}
              </div>
              <div style={{ marginTop: '20px' }}>
                {!aiExplanation && !aiLoading && (<motion.button onClick={handleAIExplain} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: '14px 24px', fontSize: '15px', fontWeight: 600, borderRadius: '12px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: 'white', boxShadow: '0 4px 15px rgba(249,115,22,0.3)', display: 'flex', alignItems: 'center', gap: '8px' }}>AI Explanation</motion.button>)}
                {aiLoading && (<div style={{ marginTop: '20px', textAlign: 'center', padding: '20px' }}><Lottie animationData={generateAIAnim} style={{ width: 120, height: 120, margin: '0 auto' }} loop={true} /><p style={{ color: '#6366f1', marginTop: '10px', fontWeight: 600, fontSize: '14px' }}>Generating...</p></div>)}
                {aiExplanation && (<div style={{ marginTop: '20px', background: '#f8f9fc', padding: '20px', borderRadius: '16px', border: '1px solid #e5e7eb' }}><div style={{ fontSize: '12px', fontWeight: 700, color: '#059669', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ fontSize: '14px' }}>🤖</span> AI Explanation</div><AIExplanationRenderer content={aiExplanation} />{remainingFreeUses !== null && (<div style={{ marginTop: "16px", padding: "10px 14px", background: "#fffbeb", borderRadius: "8px", fontSize: "13px", color: "#92400e", fontWeight: 500, border: '1px solid #fde68a' }}>{remainingFreeUses} remaining</div>)}</div>)}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                {currentIndex > 0 && (<motion.button onClick={handlePrevious} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: '1px solid #e5e7eb', cursor: 'pointer', background: '#f3f4f6', color: '#6b7280', flex: 1 }}>Previous</motion.button>)}
                <motion.button onClick={handleNext} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: '12px 20px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', boxShadow: '0 4px 15px rgba(16,185,129,0.3)', flex: 1 }}>{currentIndex === questions.length - 1 ? 'View All' : 'Next'}</motion.button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
    </MathJaxContext>
  );
}

export default QuestionList;